"use client"

import { AlertTriangle, CheckCircle2, ListChecks, Target } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import type { SystemAnalysisPayload } from "@/lib/agent-force"

function Section({
  title,
  items,
  tone = "neutral",
}: {
  title: string
  items: string[]
  tone?: "neutral" | "warning" | "good"
}) {
  const Icon = tone === "warning" ? AlertTriangle : tone === "good" ? CheckCircle2 : ListChecks
  const toneClass =
    tone === "warning"
      ? "text-negative"
      : tone === "good"
        ? "text-positive"
        : "text-muted-foreground"
  return (
    <div className="rounded-lg border border-border bg-secondary/20 p-3">
      <div className="mb-2 flex items-center gap-2">
        <Icon className={`h-4 w-4 ${toneClass}`} />
        <p className="text-sm font-semibold">{title}</p>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground">No notable entries.</p>
      ) : (
        <ul className="space-y-1">
          {items.map((line, i) => (
            <li key={`${title}-${i}`} className="text-xs text-muted-foreground">
              - {line}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function AgentPredictions({ analysis }: { analysis: SystemAnalysisPayload | null }) {
  const summary = analysis?.summary ?? "Waiting for system-analysis payload."
  const what = analysis?.what_the_log_is_showing ?? []
  const problems = analysis?.specific_problems_visible ?? []
  const good = analysis?.good_news_in_this_trace ?? []

  return (
    <Card className="min-w-0 w-full bg-card border-border">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" />
          System Analysis Agent
        </CardTitle>
        <Badge variant="outline" className="text-xs">
          Log Interpretation
        </Badge>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="rounded-lg border border-border bg-secondary/20 p-3">
            <p className="text-sm font-semibold">Summary</p>
            <p className="mt-1 text-xs text-muted-foreground">{summary}</p>
          </div>
          <Section title="What the log is showing" items={what} />
          <Section title="logs" items={problems} tone="warning" />
          <Section title="Good news in this trace" items={good} tone="good" />
        </div>
      </CardContent>
    </Card>
  )
}
